import { useAllAddresses } from 'app/network/address';
import { useAtom } from 'jotai/index';
import { useEffect } from 'react';
import { getSignalData, hasSignal, Signal } from 'app/network/signals';
import { needsAppUpdateAtom } from 'app/atoms/app';
import Constants from 'expo-constants';
import { Linking, Platform } from 'react-native';
import { isMobileNative } from 'app/utils/web-helpers';
import { appStoreUrls } from 'app/lib/app-store';

function isVersionSufficient(currentVersion: string, minimumAppVersion: string) {
  const currentParts = currentVersion.split('.').map((s) => parseInt(s));
  const minimumParts = minimumAppVersion.split('.').map((s) => parseInt(s));

  if (currentParts.length !== minimumParts.length) {
    return true;
  }

  for (let i = 0; i < currentParts.length; i++) {
    const current = currentParts[i] || 0;
    const minimum = minimumParts[i] || 0;
    if (current > minimum) {
      return true;
    }
    if (current < minimum) {
      return false;
    }
  }

  return true;
}

function validVersionFormat(version: string) {
  if (!version) return false;
  const versionRegex = /^\d+(\.\d+){0,2}$/;
  return versionRegex.test(version);
}

interface MinimumAppVersionSignalData {
  minimumAppVersion: string;
}

export function useNeedsAppUpdate() {
  const platformSpecificUrls = appStoreUrls;

  const appStoreUrl = Platform.select({ ...platformSpecificUrls });

  const openAppStore = async (url = appStoreUrl) => {
    try {
      if (!url) return;
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        console.error(`Cannot open URL: ${url}`);
      }
    } catch (error) {
      console.error(`Error opening ${Platform.OS} store:`, error);
    }
  };

  const { data: addresses } = useAllAddresses();
  const firstAddress = addresses?.[0];

  const [needsAppUpdate, setNeedsAppUpdate] = useAtom(needsAppUpdateAtom);

  const currentVersion = Constants.expoConfig?.version;
  useEffect(() => {
    if (!currentVersion || !isMobileNative) return;
    if (!hasSignal(firstAddress, Signal.NeedAppUpdate)) return;

    const signalData = getSignalData(firstAddress, Signal.NeedAppUpdate) as MinimumAppVersionSignalData;

    if (validVersionFormat(signalData.minimumAppVersion)) {
      setNeedsAppUpdate(!isVersionSufficient(currentVersion, signalData.minimumAppVersion));
    }
  }, [firstAddress, currentVersion, setNeedsAppUpdate]);

  return { needsAppUpdate, openAppStore, appStoreUrl, possibleUrls: platformSpecificUrls };
}
